import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import EnquiryCard from './components/EnquiryCard';
import PerformanceStats from './components/PerformanceStats';
import EnquiryFilters from './components/EnquiryFilters';
import RecentMessages from './components/RecentMessages';
import QuickActions from './components/QuickActions';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import EnquiryAcceptancePayment from '../../components/payment/EnquiryAcceptancePayment';
import { paymentService } from '../../services/payments';
import { getOrCreateConversationForEnquiry } from '../../services/messageService';

const AgentDashboard = () => {
  const { user, userProfile, loading: authLoading, sessionChecked } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('proposed');
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  
  // Separated loading states for better control
  const [loadingStates, setLoadingStates] = useState({
    profile: true,
    proposedEnquiries: true,
    acceptedEnquiries: true,
    stats: false,
    messages: false
  });
  
  const [error, setError] = useState(null);
  
  // Real data states
  const [agentProfile, setAgentProfile] = useState(null);
  const [proposedEnquiries, setProposedEnquiries] = useState([]);
  const [acceptedEnquiries, setAcceptedEnquiries] = useState([]);
  const [performanceStats, setPerformanceStats] = useState({
    received: 0,
    accepted: 0,
    inDiscussion: 0,
    booked: 0,
    totalRevenue: 0,
    conversionRate: 0,
    successRate: 0
  });
  const [recentMessages, setRecentMessages] = useState([]);
  const [agentBillingStatus, setAgentBillingStatus] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);

  // ✅ NEW: Add state for tracking accepting enquiries
  const [acceptingEnquiry, setAcceptingEnquiry] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedEnquiryForPayment, setSelectedEnquiryForPayment] = useState(null);

  // ✅ NEW: Per-row loading and error states for opening conversations
  const [openingConversation, setOpeningConversation] = useState(null); // enquiry ID currently being opened
  const [conversationError, setConversationError] = useState(null); // { enquiryId, message }

  // Helper to update loading state
  const updateLoadingState = (key, value) => {
    console.log(`[Loading State] ${key}: ${value}`);
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  // Computed loading state for enquiries display
  const isLoadingEnquiries = activeTab === 'proposed' 
    ? loadingStates?.profile || loadingStates?.proposedEnquiries
    : loadingStates?.acceptedEnquiries;

  // Fetch agent billing status
  useEffect(() => {
    const fetchBillingStatus = async () => {
      if (!user?.id) return;

      setLoadingBilling(true);
      try {
        const status = await paymentService?.getAgentBillingStatus(user?.id);
        setAgentBillingStatus(status);
      } catch (err) {
        console.error('[Agent Dashboard] Error loading billing status:', err);
      } finally {
        setLoadingBilling(false);
      }
    };

    fetchBillingStatus();
  }, [user?.id]);

  // ✅ UPDATED: Enhanced authentication and status check with BOTH status and verified
  useEffect(() => {
    if (!sessionChecked || authLoading) return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (userProfile?.role !== 'agent') {
      navigate('/', { replace: true });
      return;
    }

    // ✅ CRITICAL FIX: Check agent status for access control
    const checkAgentStatus = async () => {
      try {
        // Fetch agent_profiles data
        const { data: agentProfile, error: profileError } = await supabase
          ?.from('agent_profiles')
          ?.select('status')
          ?.eq('user_id', user?.id)
          ?.single();

        if (profileError) {
          console.error('Error checking agent status:', profileError);
          // If no agent profile exists, redirect to registration
          if (profileError?.code === 'PGRST116') {
            navigate('/agent-registration', { replace: true });
          }
          return;
        }

        console.log('Agent access control check:', {
          agent_profiles_status: agentProfile?.status,
          access_granted: agentProfile?.status === 'approved'
        });

        if (agentProfile?.status === 'pending') {
          console.log('Agent status is pending, redirecting to vetting');
          navigate('/agent-vetting', { replace: true });
        } else if (agentProfile?.status === 'rejected') {
          console.log('Agent status is rejected, redirecting to rejection notice');
          navigate('/agent-rejection-notice', { replace: true });
        }
        // If status is 'approved', allow access to dashboard (no redirect)
      } catch (error) {
        console.error('Unexpected error checking agent status:', error);
      }
    };

    checkAgentStatus();
  }, [user, userProfile, authLoading, sessionChecked, navigate]);

  // Fetch agent profile with specializations
  useEffect(() => {
    const fetchAgentProfile = async () => {
      if (!user?.id) {
        console.log('[Agent Profile] No user ID, skipping fetch');
        updateLoadingState('profile', false);
        return;
      }

      console.log('[Agent Profile] Fetching profile for user:', user?.id);
      updateLoadingState('profile', true);
      
      try {
        const { data, error } = await supabase
          ?.from('agent_profiles')
          ?.select('*')
          ?.eq('user_id', user?.id)
          ?.single();

        if (error) {
          console.error('[Agent Profile] Error fetching:', error);
          setError('Unable to load your agent profile. Please ensure your registration is complete.');
          setAgentProfile(null);
        } else if (!data) {
          console.warn('[Agent Profile] No profile found');
          setError('Agent profile not found. Please complete your registration.');
          setAgentProfile(null);
        } else {
          console.log('[Agent Profile] Profile loaded:', {
            hasSpecializations: data?.specializations?.length > 0,
            specializationCount: data?.specializations?.length
          });
          setAgentProfile(data);
          setError(null);
        }
      } catch (error) {
        console.error('[Agent Profile] Unexpected error:', error);
        setError('An unexpected error occurred while loading your profile.');
        setAgentProfile(null);
      } finally {
        // CRITICAL: Always resolve loading state
        updateLoadingState('profile', false);
      }
    };

    fetchAgentProfile();
  }, [user?.id]);

  // Fetch proposed enquiries - only runs after profile loads
  useEffect(() => {
    const fetchProposedEnquiries = async () => {
      // Wait for profile to finish loading first
      if (loadingStates?.profile) {
        console.log('[Proposed Enquiries] Waiting for profile to load');
        return;
      }

      if (!user?.id) {
        console.log('[Proposed Enquiries] No user ID, showing empty state');
        setProposedEnquiries([]);
        updateLoadingState('proposedEnquiries', false);
        return;
      }
      
      // If agent profile doesn't exist, show empty state
      if (!agentProfile) {
        console.log('[Proposed Enquiries] No agent profile, showing empty state');
        setProposedEnquiries([]);
        updateLoadingState('proposedEnquiries', false);
        return;
      }
      
      // If no specializations, show informative empty state
      if (!agentProfile?.specializations || agentProfile?.specializations?.length === 0) {
        console.log('[Proposed Enquiries] No specializations configured');
        setProposedEnquiries([]);
        // Don't override profile-related errors
        if (!error?.includes('profile')) {
          setError('No specializations configured. Please update your profile to start receiving enquiries.');
        }
        updateLoadingState('proposedEnquiries', false);
        return;
      }

      console.log('[Proposed Enquiries] Fetching enquiries with params:', {
        agentId: user?.id,
        specializations: agentProfile?.specializations,
        countriesOperated: agentProfile?.countries_operated
      });
      
      updateLoadingState('proposedEnquiries', true);
      
      try {
        // Calculate 14-day expiry date
        const fourteenDaysAgo = new Date();
        fourteenDaysAgo?.setDate(fourteenDaysAgo?.getDate() - 14);

        // ✅ FIX: Use NOT EXISTS to exclude enquiries already accepted by this agent
        const { data, error } = await supabase
          ?.from('enquiries')
          ?.select(`
            *,
            customer:user_profiles!enquiries_customer_id_fkey(
              id,
              full_name,
              email,
              phone
            )
          `)
          ?.eq('status', 'proposed')
          ?.gte('created_at', fourteenDaysAgo?.toISOString())
          ?.order('created_at', { ascending: false });

        if (error) {
          console.error('[Proposed Enquiries] Error fetching:', error);
          setError('Unable to load proposed enquiries. Please try again.');
          setProposedEnquiries([]);
        } else {
          console.log('[Proposed Enquiries] Fetched:', data?.length, 'enquiries');
          
          // Filter out enquiries this agent has already paid for
          const { data: agentAccess, error: accessError } = await supabase
            ?.from('agent_enquiry_access')
            ?.select('enquiry_id')
            ?.eq('agent_id', user?.id);

          if (accessError) {
            console.error('[Proposed Enquiries] Error checking agent access:', accessError);
          }

          const accessedEnquiryIds = new Set(agentAccess?.map(a => a?.enquiry_id) || []);
          const filteredEnquiries = data?.filter(e => !accessedEnquiryIds?.has(e?.id)) || [];

          console.log('[Proposed Enquiries] After filtering already accessed:', filteredEnquiries?.length, 'enquiries');
          setProposedEnquiries(filteredEnquiries);
          
          // Clear error if successful
          if (!error?.includes('profile')) {
            setError(null);
          }
        }
      } catch (error) {
        console.error('[Proposed Enquiries] Unexpected error:', error);
        setError('An unexpected error occurred while loading enquiries.');
        setProposedEnquiries([]);
      } finally {
        updateLoadingState('proposedEnquiries', false);
      }
    };

    fetchProposedEnquiries();
  }, [user?.id, agentProfile, loadingStates?.profile]);

  // Fetch accepted enquiries - enquiries this agent has paid to access
  useEffect(() => {
    const fetchAcceptedEnquiries = async () => {
      if (!user?.id) {
        console.log('[Accepted Enquiries] No user ID');
        setAcceptedEnquiries([]);
        updateLoadingState('acceptedEnquiries', false);
        return;
      }

      console.log('[Accepted Enquiries] Fetching for agent:', user?.id);
      updateLoadingState('acceptedEnquiries', true);
      
      try {
        // Get enquiries this agent has paid to access via agent_enquiry_access
        const { data: accessRecords, error: accessError } = await supabase
          ?.from('agent_enquiry_access')
          ?.select('enquiry_id')
          ?.eq('agent_id', user?.id);

        if (accessError) {
          console.error('[Accepted Enquiries] Error fetching access records:', accessError);
          setAcceptedEnquiries([]);
          updateLoadingState('acceptedEnquiries', false);
          return;
        }

        const enquiryIds = accessRecords?.map(a => a?.enquiry_id) || [];

        if (enquiryIds?.length === 0) {
          console.log('[Accepted Enquiries] No accessed enquiries found');
          setAcceptedEnquiries([]);
          updateLoadingState('acceptedEnquiries', false);
          return;
        }

        // Fetch full enquiry details for accessed enquiries
        const { data, error } = await supabase
          ?.from('enquiries')
          ?.select(`
            *,
            customer:user_profiles!enquiries_customer_id_fkey(
              id,
              full_name,
              email,
              phone,
              created_at
            ),
            conversation:conversations!conversations_enquiry_id_fkey(
              id,
              status,
              quote_price,
              quote_currency,
              quote_submitted_at,
              last_message_at
            )
          `)
          ?.in('id', enquiryIds)
          ?.order('created_at', { ascending: false });

        if (error) {
          console.error('[Accepted Enquiries] Error fetching:', error);
          setAcceptedEnquiries([]);
        } else {
          console.log('[Accepted Enquiries] Fetched:', data?.length, 'enquiries');
          
          // Filter to only show conversations for THIS agent
          const enrichedData = data?.map(enquiry => ({
            ...enquiry,
            conversation: enquiry?.conversation?.filter(c => c?.agent_id === user?.id) || []
          })) || [];

          setAcceptedEnquiries(enrichedData);
        }
      } catch (error) {
        console.error('[Accepted Enquiries] Unexpected error:', error);
        setAcceptedEnquiries([]);
      } finally {
        updateLoadingState('acceptedEnquiries', false);
      }
    };

    fetchAcceptedEnquiries();
  }, [user?.id]);

  // Fetch performance statistics
  useEffect(() => {
    const fetchPerformanceStats = async () => {
      if (!user?.id) return;

      try {
        // Count enquiries received by this agent (via conversations)
        const { count: receivedCount } = await supabase
          ?.from('conversations')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('agent_id', user?.id);

        // Count accepted enquiries
        const { count: acceptedCount } = await supabase?.from('enquiries')?.select('id', { count: 'exact', head: true })?.eq('agent_id', user?.id)?.in('status', ['assigned', 'in_progress']);

        // Count in discussion (accepted with active conversations)
        const { count: inDiscussionCount } = await supabase?.from('enquiries')?.select('id', { count: 'exact', head: true })?.eq('agent_id', user?.id)?.eq('status', 'in_progress');

        // Count completed/booked
        const { count: bookedCount } = await supabase?.from('enquiries')?.select('id', { count: 'exact', head: true })?.eq('agent_id', user?.id)?.eq('status', 'completed');

        // Calculate revenue from completed bookings with error handling
        let totalRevenue = 0;
        try {
          const { data: completedEnquiries, error: paymentError } = await supabase?.from('payment_transactions')?.select('amount')?.eq('user_id', user?.id)?.eq('status', 'succeeded');
          
          if (paymentError) {
            console.warn('[Performance Stats] Payment transactions query failed:', paymentError);
            // Continue without revenue data instead of failing
          } else {
            totalRevenue = completedEnquiries?.reduce((sum, t) => sum + (t?.amount || 0), 0) / 100 || 0;
          }
        } catch (err) {
          console.warn('[Performance Stats] Error fetching payment data:', err);
          // Continue without revenue data
        }

        // Calculate conversion rate
        const conversionRate = acceptedCount > 0 
          ? Math.round((bookedCount / acceptedCount) * 100) 
          : 0;

        // Calculate success rate (completed vs total accepted)
        const successRate = acceptedCount > 0
          ? Math.round((bookedCount / acceptedCount) * 100)
          : 0;

        setPerformanceStats({
          received: receivedCount || 0,
          accepted: acceptedCount || 0,
          inDiscussion: inDiscussionCount || 0,
          booked: bookedCount || 0,
          totalRevenue: totalRevenue,
          conversionRate: conversionRate,
          successRate: successRate
        });
      } catch (error) {
        console.error('Error fetching performance stats:', error);
        // Don't set error state - just log and continue with default stats
      }
    };

    fetchPerformanceStats();
  }, [user?.id]);

  // Fetch recent messages
  useEffect(() => {
    const fetchRecentMessages = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase?.from('messages')?.select(`
            *,
            conversation:conversations(
              id,
              enquiry:enquiries(
                id,
                destination,
                customer:user_profiles!enquiries_customer_id_fkey(
                  full_name
                )
              )
            )
          `)?.neq('sender_id', user?.id)?.order('created_at', { ascending: false })?.limit(5);

        if (error) throw error;

        const formattedMessages = data?.map(msg => ({
          id: msg?.id,
          conversationId: msg?.conversation_id,
          customerName: msg?.conversation?.enquiry?.customer_name || msg?.conversation?.enquiry?.customer?.full_name || 'Unknown',
          enquiryDestination: msg?.conversation?.enquiry?.destination || 'Unknown',
          content: msg?.content,
          timestamp: new Date(msg?.created_at),
          read: msg?.is_read,
          isFromCustomer: true,
          messageType: msg?.message_type || 'text',
          hasAttachment: !!msg?.file_url
        })) || [];

        setRecentMessages(formattedMessages);
      } catch (error) {
        console.error('Error fetching recent messages:', error);
        setRecentMessages([]);
      }
    };

    fetchRecentMessages();
  }, [user?.id]);

  const tabs = [
    { 
      key: 'proposed', 
      label: 'Proposed for You', 
      count: proposedEnquiries?.length,
      icon: 'Heart'
    },
    { 
      key: 'accepted', 
      label: 'Accepted Enquiries', 
      count: acceptedEnquiries?.length,
      icon: 'CheckCircle'
    }
  ];

  // ✅ UPDATED: Handle accept enquiry with card-on-file
  const handleAcceptEnquiry = async (enquiry) => {
    // Check if billing is ready
    if (!agentBillingStatus?.billing_ready) {
      // Redirect to settings to add payment method
      navigate('/agent-account-settings');
      return;
    }

    setAcceptingEnquiry(enquiry?.id);
    setSelectedEnquiryForPayment(enquiry);

    try {
      console.log('💳 [Payment] Starting enquiry acceptance payment for:', enquiry?.id);

      // Create payment intent for enquiry acceptance
      const paymentResult = await paymentService?.createEnquiryAcceptancePayment(
        user?.id,
        enquiry?.id
      );

      console.log('💳 [Payment] Payment result:', paymentResult);

      if (paymentResult?.success) {
        console.log('✅ [Payment] Payment succeeded, refetching accepted enquiries');

        // ✅ CRITICAL FIX: Refetch accepted enquiries to get the conversation created by webhook
        // Wait a moment for webhook to complete
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Refetch accepted enquiries from database
        const fourteenDaysAgo = new Date();
        fourteenDaysAgo?.setDate(fourteenDaysAgo?.getDate() - 14);

        const { data: acceptedData, error: acceptedError } = await supabase
          ?.from('enquiries')
          ?.select(`
            *,
            customer:user_profiles!enquiries_customer_id_fkey(
              id,
              full_name,
              email,
              avatar_url,
              created_at
            ),
            conversation:conversations!conversations_enquiry_id_fkey(
              id,
              status,
              last_message_at,
              created_at,
              quote_price,
              quote_currency,
              quote_submitted_at
            )
          `)
          ?.eq('status', 'assigned')
          ?.eq('agent_id', user?.id)
          ?.gte('created_at', fourteenDaysAgo?.toISOString())
          ?.order('created_at', { ascending: false });

        if (acceptedError) {
          console.error('[Payment Success] Error refetching accepted enquiries:', acceptedError);
          throw new Error('Failed to load updated enquiries');
        }

        // Get unread message counts
        const enquiriesWithMessages = await Promise.all(
          (acceptedData || [])?.map(async (enq) => {
            const customerName = enq?.customer_name ||
                                enq?.customer?.full_name ||
                                enq?.customer?.email ||
                                'Unknown user';

            const conversationArray = Array.isArray(enq?.conversation)
              ? enq?.conversation
              : (enq?.conversation ? [enq?.conversation] : []);

            const conversation = conversationArray?.[0];

            let unreadCount = 0;
            if (conversation?.id) {
              const { count } = await supabase
                ?.from('messages')
                ?.select('id', { count: 'exact', head: true })
                ?.eq('conversation_id', conversation?.id)
                ?.eq('is_read', false)
                ?.neq('sender_id', user?.id);

              unreadCount = count || 0;
            }

            return {
              ...enq,
              customer: {
                ...enq?.customer,
                name: customerName,
                joinedDate: enq?.customer?.created_at
              },
              conversation: conversationArray,
              unreadMessages: unreadCount,
              conversationStatus: unreadCount > 0 ? 'unread' : 'read'
            };
          })
        );

        console.log('[Payment Success] Refetched accepted enquiries:', enquiriesWithMessages?.length);
        console.log('[Payment Success] Conversation IDs:', enquiriesWithMessages?.map(e => ({
          enquiry_id: e?.id,
          conversation_id: e?.conversation?.[0]?.id
        })));

        setAcceptedEnquiries(enquiriesWithMessages);

        // Remove from proposed enquiries
        setProposedEnquiries(prev => prev?.filter(e => e?.id !== enquiry?.id));

        // Switch to accepted tab
        setActiveTab('accepted');

        // Show success message inline (no alert)
        console.log('[Agent Dashboard] Enquiry accepted successfully');
      } else {
        // Payment failed or requires action
        setError('Payment failed. Please check your payment method in Settings.');
      }
    } catch (error) {
      console.error('Error accepting enquiry:', error);
      setError(error?.message || 'Failed to accept enquiry. Please try again.');
    } finally {
      setAcceptingEnquiry(null);
      setSelectedEnquiryForPayment(null);
    }
  };

  const handlePaymentSuccess = async (paymentResult) => {
    console.log('Payment successful, proceeding with enquiry acceptance:', paymentResult);
    
    const enquiry = selectedEnquiryForPayment;
    setAcceptingEnquiry(enquiry?.id);
    setShowPaymentModal(false);
    
    try {
      console.log('[DIAGNOSTIC] Agent accepting enquiry after payment:', {
        agentId: user?.id,
        enquiryId: enquiry?.id,
        customerId: enquiry?.customer_id,
        enquiryTitle: enquiry?.title,
        paymentIntentId: paymentResult?.paymentIntent?.id
      });

      const conversationData = {
        enquiry_id: enquiry?.id,
        customer_id: enquiry?.customer_id,
        agent_id: user?.id,
        title: `Enquiry: ${enquiry?.destination}`,
        status: 'open'
      };

      console.log('[DIAGNOSTIC] Creating conversation with data:', conversationData);

      const { data: conversation, error: convError } = await supabase
        ?.from('conversations')
        ?.upsert(
          conversationData,
          {
            onConflict: 'agent_id,enquiry_id',
            ignoreDuplicates: false
          }
        )
        ?.select()
        ?.single();

      if (convError) {
        console.error('[DIAGNOSTIC] Conversation creation error:', convError);
        throw convError;
      }

      console.log('[DIAGNOSTIC] Conversation created/found:', {
        conversationId: conversation?.id,
        customerId: conversation?.customer_id,
        agentId: conversation?.agent_id,
        enquiryId: conversation?.enquiry_id,
        title: conversation?.title
      });

      // Create conversation participants with correct roles
      console.log('[DIAGNOSTIC] Creating conversation participants:', {
        conversationId: conversation?.id,
        customerId: enquiry?.customer_id,
        agentId: user?.id
      });

      const { error: participantsError } = await supabase
        ?.from('conversation_participants')
        ?.upsert(
          [
            {
              conversation_id: conversation?.id,
              user_id: enquiry?.customer_id,
              role_in_conversation: 'customer',
              is_active: true
            },
            {
              conversation_id: conversation?.id,
              user_id: user?.id,
              role_in_conversation: 'agent',
              is_active: true
            }
          ],
          {
            onConflict: 'conversation_id,user_id',
            ignoreDuplicates: false
          }
        );

      if (participantsError) {
        console.error('[DIAGNOSTIC] Error creating participants:', participantsError);
        // Don't throw - participants might already exist
      } else {
        console.log('[DIAGNOSTIC] Conversation participants created successfully');
      }

      const { error: updateError } = await supabase
        ?.from('enquiries')
        ?.update({ 
          status: 'assigned',
          agent_id: user?.id
        })
        ?.eq('id', enquiry?.id);

      if (updateError) throw updateError;

      const updatedProposed = proposedEnquiries?.filter(e => e?.id !== enquiry?.id);
      setProposedEnquiries(updatedProposed);
      
      // ✅ FIX: Use the full conversation object returned from database
      const newAcceptedEnquiry = {
        ...enquiry,
        status: 'assigned',
        agent_id: user?.id,
        conversation: [{ 
          id: conversation?.id, 
          status: conversation?.status || 'open', 
          last_message_at: conversation?.last_message_at,
          created_at: conversation?.created_at,
          quote_price: conversation?.quote_price,
          quote_currency: conversation?.quote_currency,
          quote_submitted_at: conversation?.quote_submitted_at
        }],
        customer: {
          ...enquiry?.customer,
          name: enquiry?.customer?.full_name || enquiry?.customer?.email || 'Unknown user',
          joinedDate: enquiry?.customer?.created_at
        },
        unreadMessages: 0,
        conversationStatus: 'read'
      };
      
      const updatedAccepted = [newAcceptedEnquiry, ...acceptedEnquiries];
      setAcceptedEnquiries(updatedAccepted);

      console.log('📊 [After Accept] Proposed:', updatedProposed?.length, 'Accepted:', updatedAccepted?.length);
      console.log('✅ Change: Proposed -1, Accepted +2');
      console.log('[DIAGNOSTIC] New accepted enquiry with conversation:', {
        enquiryId: newAcceptedEnquiry?.id,
        conversationId: newAcceptedEnquiry?.conversation?.[0]?.id,
        hasConversation: !!newAcceptedEnquiry?.conversation?.[0]?.id
      });

      console.log('[Agent Dashboard] Enquiry accepted successfully');

    } catch (error) {
      console.error('[DIAGNOSTIC] Error accepting enquiry:', error);
      setError(`Failed to accept enquiry: ${error?.message}`);

      try {
        const fourteenDaysAgo = new Date();
        fourteenDaysAgo?.setDate(fourteenDaysAgo?.getDate() - 14);

        const { data: conversationsData } = await supabase
          ?.from('conversations')
          ?.select(`
            id,
            enquiry_id,
            status,
            last_message_at,
            created_at,
            quote_price,
            quote_currency,
            quote_submitted_at,
            enquiry:enquiries!conversations_enquiry_id_fkey (
              id,
              title,
              destination,
              departure_date,
              return_date,
              budget_range,
              trip_type,
              adults,
              children,
              preferred_nights,
              status,
              customer_id,
              customer:user_profiles!enquiries_customer_id_fkey (
                id,
                full_name,
                email,
                avatar_url,
                created_at
              )
            )
          `)
          ?.eq('agent_id', user?.id)
          ?.gte('created_at', fourteenDaysAgo?.toISOString())
          ?.order('created_at', { ascending: false });

        if (conversationsData) {
          const refetchedAccepted = conversationsData
            ?.map(conv => ({
              ...conv?.enquiry,
              conversation: [conv],
              customer: conv?.enquiry?.customer,
              unreadMessages: 0,
              conversationStatus: 'read'
            }))
            ?.filter(e => e?.id);
          
          setAcceptedEnquiries(refetchedAccepted);
        }
      } catch (refetchError) {
        console.error('Failed to refetch after error:', refetchError);
      }
    } finally {
      setAcceptingEnquiry(null);
      setSelectedEnquiryForPayment(null);
    }
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setSelectedEnquiryForPayment(null);
    setAcceptingEnquiry(null);
  };

  const handleDismissEnquiry = (enquiry) => {
    // Remove confirmation popup, just dismiss directly
    console.log('[Agent Dashboard] Dismissing enquiry:', enquiry?.id);
    setProposedEnquiries(prev => prev?.filter(e => e?.id !== enquiry?.id));
  };

  const handleViewMessages = async (enquiry) => {
    try {
      console.log('[Agent Dashboard] Opening messages for enquiry:', {
        enquiryId: enquiry?.id,
        destination: enquiry?.destination,
        status: enquiry?.status
      });

      // Clear any previous error for this enquiry
      setConversationError(null);
      
      // Set loading state for this specific enquiry
      setOpeningConversation(enquiry?.id);

      // Get or create conversation for this enquiry (uses cache for instant navigation)
      const conversationId = await getOrCreateConversationForEnquiry(enquiry?.id);

      if (!conversationId) {
        console.error('[Agent Dashboard] No conversation ID returned for enquiry:', enquiry?.id);
        setConversationError({ 
          enquiryId: enquiry?.id, 
          message: 'Failed to create or find conversation.' 
        });
        setOpeningConversation(null);
        return;
      }

      console.log('[Agent Dashboard] Successfully got conversation ID:', {
        conversationId,
        enquiryId: enquiry?.id
      });

      // Navigate to agent v2 chat thread route
      window.location.href = `/agent/messages-v2/${conversationId}`;
    } catch (error) {
      console.error('[Agent Dashboard] Error opening messages:', {
        error,
        errorMessage: error?.message,
        enquiryId: enquiry?.id
      });
      
      // Show inline error instead of alert
      const errorMessage = error?.message || 'Unknown error occurred';
      setConversationError({ 
        enquiryId: enquiry?.id, 
        message: errorMessage 
      });
      setOpeningConversation(null);
    }
  };

  const handleEditEnquiry = (enquiry) => {
    window.location.href = `/enquiries/${enquiry?.id}/edit`;
  };

  const handleCloseEnquiry = async (enquiry) => {
    // Remove confirmation popup, close directly
    try {
      const { error } = await supabase?.from('enquiries')?.update({ status: 'completed' })?.eq('id', enquiry?.id);

      if (error) throw error;

      console.log('[Agent Dashboard] Enquiry closed successfully');
      window.location?.reload();
    } catch (error) {
      console.error('Error closing enquiry:', error);
      setError('Failed to close enquiry. Please try again.');
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleViewAllMessages = () => {
    // ✅ UPDATED: Navigate to agent v2 messaging inbox using /agent/messages route alias
    window.location.href = '/agent/messages';
  };

  const handleLogout = () => {
    localStorage.removeItem('holiyay_user');
    window.location.href = '/';
  };

  const filteredEnquiries = activeTab === 'proposed' ? proposedEnquiries : acceptedEnquiries;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Payment Modal */}
      {showPaymentModal && selectedEnquiryForPayment && (
        <EnquiryAcceptancePayment
          enquiry={selectedEnquiryForPayment}
          agentInfo={{
            id: user?.id,
            full_name: user?.user_metadata?.full_name || user?.email,
            email: user?.email,
            phone: user?.user_metadata?.phone,
            business_address: agentProfile?.business_address,
            location: user?.user_metadata?.location,
            stripe_customer_id: null
          }}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
          isOpen={showPaymentModal}
        />
      )}

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Welcome Section */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                  Welcome back, {userProfile?.full_name || 'Agent'}
                </h1>
                <p className="text-muted-foreground">
                  {userProfile?.company_name || 'Travel Agent'} • {userProfile?.verified ? 'Verified Agent' : 'Pending Verification'}
                </p>
              </div>
              
              <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                <Button
                  variant="outline"
                  onClick={() => navigate('/agent-account-settings')}
                  iconName="Settings"
                  iconPosition="left"
                  iconSize={16}
                >
                  Profile Settings
                </Button>
                <Button
                  variant="default"
                  onClick={() => window.location.href = '/agent/messages'}
                  iconName="MessageCircle"
                  iconPosition="left"
                  iconSize={16}
                >
                  Messages
                </Button>
              </div>
            </div>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="mb-6 bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-error mb-1">Unable to Load Data</h3>
                  <p className="text-sm text-error/80">{error}</p>
                  <div className="flex items-center space-x-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location?.reload()}
                      iconName="RefreshCw"
                      iconPosition="left"
                      iconSize={14}
                    >
                      Refresh
                    </Button>
                    {error?.includes('profile') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.location.href = '/agent-registration'}
                        iconName="UserPlus"
                        iconPosition="left"
                        iconSize={14}
                      >
                        Complete Registration
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Main Content Area */}
            <div className="xl:col-span-3 space-y-6">
              {/* Enquiry Tabs */}
              <div className="bg-card border border-border rounded-lg">
                {/* Tab Navigation */}
                <div className="border-b border-border">
                  <div className="flex flex-col sm:flex-row">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.key}
                        onClick={() => setActiveTab(tab?.key)}
                        className={`flex items-center justify-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                          activeTab === tab?.key
                            ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                      >
                        <Icon name={tab?.icon} size={16} />
                        <span>{tab?.label}</span>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          activeTab === tab?.key
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {tab?.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Filters Toggle */}
                <div className="p-4 border-b border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    iconName="Filter"
                    iconPosition="left"
                    iconSize={16}
                  >
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                  </Button>
                </div>

                {/* Filters */}
                {showFilters && (
                  <EnquiryFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onClearFilters={handleClearFilters}
                    className="border-b border-border"
                  />
                )}

                {/* Enquiries List */}
                <div className="space-y-4">
                  {isLoadingEnquiries ? (
                    <div className="text-center py-12">
                      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-muted-foreground">Loading enquiries...</p>
                    </div>
                  ) : (
                    <>
                      {activeTab === 'proposed' && (
                        <>
                          {proposedEnquiries?.length === 0 ? (
                            <div className="text-center py-12">
                              <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                              <h3 className="text-lg font-medium text-foreground mb-2">
                                No enquiries found
                              </h3>
                              <p className="text-muted-foreground mb-4">
                                Please add specialisations to your profile to start receiving enquiries.
                              </p>
                              <Button
                                variant="default"
                                onClick={() => navigate('/agent-account-settings')}
                                iconName="Settings"
                                iconPosition="left"
                                iconSize={16}
                              >
                                Update Profile
                              </Button>
                            </div>
                          ) : (
                            proposedEnquiries?.map((enquiry) => (
                              <EnquiryCard
                                key={enquiry?.id}
                                enquiry={enquiry}
                                type="available"
                                onAccept={() => handleAcceptEnquiry(enquiry)}
                                onDismiss={() => handleDismissEnquiry(enquiry?.id)}
                                isAccepting={acceptingEnquiry === enquiry?.id}
                                agentBillingStatus={agentBillingStatus}
                                onViewMessages={() => {}}
                                onEditEnquiry={() => {}}
                                onCloseEnquiry={() => {}}
                                isOpeningConversation={openingConversation === enquiry?.id}
                                conversationError={conversationError?.enquiryId === enquiry?.id ? conversationError?.message : null}
                              />
                            ))
                          )}
                        </>
                      )}

                      {activeTab === 'accepted' && (
                        <>
                          {acceptedEnquiries?.length === 0 ? (
                            <div className="text-center py-12">
                              <Icon name="MessageCircle" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                              <h3 className="text-lg font-medium text-foreground mb-2">
                                No accepted enquiries
                              </h3>
                              <p className="text-muted-foreground mb-4">
                                You haven't accepted any enquiries yet.
                              </p>
                              <Button
                                variant="outline"
                                onClick={handleClearFilters}
                                iconName="RefreshCw"
                                iconPosition="left"
                                iconSize={16}
                              >
                                Clear Filters
                              </Button>
                            </div>
                          ) : (
                            acceptedEnquiries?.map((enquiry) => (
                              <EnquiryCard
                                key={enquiry?.id}
                                enquiry={enquiry}
                                type="accepted"
                                onDismiss={() => handleDismissEnquiry(enquiry?.id)}
                                onViewDetails={handleViewMessages}
                                onViewMessages={handleViewMessages}
                                onEditEnquiry={handleEditEnquiry}
                                onCloseEnquiry={handleCloseEnquiry}
                                isAccepting={acceptingEnquiry === enquiry?.id}
                                agentBillingStatus={agentBillingStatus}
                                isOpeningConversation={openingConversation === enquiry?.id}
                                conversationError={conversationError?.enquiryId === enquiry?.id ? conversationError?.message : null}
                              />
                            ))
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Performance Stats */}
              <PerformanceStats stats={performanceStats} />

              {/* Recent Messages */}
              <RecentMessages 
                messages={recentMessages}
                onViewAll={handleViewAllMessages}
              />

              {/* Quick Actions */}
              <QuickActions />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AgentDashboard;